import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Barra di avanzamento "spedizione gratuita".
 *
 * Sorgenti di stato, in ordine:
 * 1. attributi `data-*` renderizzati da Liquid (snippets/free-shipping-bar.liquid), al load e dopo ogni morph
 *    del drawer / della pagina carrello (`updatedCallback` viene invocata da assets/morph.js);
 * 2. carrello JSON contenuto negli eventi `cart:update` e `discount:update` (`event.detail.resource`);
 * 3. fallback `fetch(/cart.js)` quando l'evento non contiene `total_price` o al ripristino da bfcache.
 *
 * La soglia (`data-threshold-eur`) è in centesimi della valuta del negozio. Se la valuta del carrello è
 * un'altra, viene convertita con `Shopify.currency.rate` (salvaguardia: tutti i mercati usano EUR).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} message - Testo di stato (role="status").
 * @property {HTMLElement} track - Traccia della barra (role="progressbar").
 *
 * @typedef {object} CartLike
 * @property {number} total_price - Totale carrello in unità minori, dopo gli sconti, in valuta di presentazione.
 * @property {number} item_count - Numero di articoli.
 * @property {boolean} requires_shipping - Se almeno un articolo richiede spedizione.
 * @property {string} [currency] - Codice ISO della valuta del carrello.
 *
 * @extends {Component<Refs>}
 */
class FreeShippingBar extends Component {
  requiredRefs = ['message', 'track'];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageShow);

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /**
   * Invocata dal morph dopo un re-render via Section Rendering API (drawer, pagina carrello):
   * gli attributi `data-*` sono già quelli nuovi calcolati da Liquid.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#render();
  }

  /**
   * Gestisce `cart:update` e `discount:update`.
   * @param {Event & { detail?: { resource?: Partial<CartLike>, data?: { didError?: boolean } } }} event
   */
  #onCartUpdate = (event) => {
    const detail = event.detail ?? {};

    // Il tema emette cart:update anche quando l'add fallisce (resource = {}): non c'è nulla da aggiornare.
    if (detail.data?.didError) return;

    const cart = detail.resource;

    if (cart && typeof cart.total_price === 'number') {
      this.#applyCart(/** @type {CartLike} */ (cart));
    } else {
      this.#fetchCart();
    }
  };

  /**
   * Ripristino da back/forward cache: il carrello potrebbe essere cambiato in un'altra pagina.
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (event.persisted) this.#fetchCart();
  };

  async #fetchCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      this.#applyCart(await response.json());
    } catch (error) {
      console.error('free-shipping-bar: cart fetch failed', error);
    }
  }

  /**
   * Copia i dati del carrello negli attributi `data-*` e ricalcola.
   * @param {CartLike} cart
   */
  #applyCart(cart) {
    this.dataset.cartTotal = String(cart.total_price);
    this.dataset.itemCount = String(cart.item_count);
    this.dataset.requiresShipping = String(cart.requires_shipping);
    if (cart.currency) this.dataset.currency = cart.currency;

    this.#render();
  }

  #render() {
    const thresholdShopCurrency = Number(this.dataset.thresholdEur);
    const total = Number(this.dataset.cartTotal) || 0;
    const itemCount = Number(this.dataset.itemCount) || 0;
    const requiresShipping = this.dataset.requiresShipping !== 'false';
    const shopCurrency = this.dataset.shopCurrency ?? '';
    const currency = this.dataset.currency || window.Shopify?.currency?.active || shopCurrency;
    const rate = currency === shopCurrency ? 1 : Number(window.Shopify?.currency?.rate);

    if (!(thresholdShopCurrency > 0) || !Number.isFinite(rate) || rate <= 0) {
      // Dati incoerenti (es. tasso assente in valuta diversa da quella del negozio):
      // meglio nascondere che mostrare un importo sbagliato.
      this.hidden = true;
      return;
    }

    const threshold = Math.round(thresholdShopCurrency * rate);
    const isEmpty = itemCount === 0;
    const hideWhenEmpty = this.dataset.hideWhenEmpty === 'true';

    if ((isEmpty && hideWhenEmpty) || (!isEmpty && !requiresShipping)) {
      this.dataset.state = isEmpty ? 'empty' : 'hidden';
      this.hidden = true;
      return;
    }

    this.hidden = false;

    const remaining = Math.max(threshold - total, 0);
    const progress = isEmpty ? 0 : Math.min(Math.round((total / threshold) * 100), 100);
    const state = isEmpty ? 'empty' : remaining === 0 ? 'reached' : 'progress';

    let text = '';

    if (state === 'reached') {
      text = this.dataset.textReached ?? '';
    } else if (state === 'empty') {
      text = (this.dataset.textEmpty ?? '').replace('[amount]', this.#formatMoney(threshold, currency));
    } else {
      text = (this.dataset.textRemaining ?? '').replace('[amount]', this.#formatMoney(remaining, currency));
    }

    this.dataset.state = state;
    this.style.setProperty('--progress', `${progress}%`);
    this.refs.track.setAttribute('aria-valuenow', String(progress));

    // Evita annunci aria-live duplicati quando il testo non cambia (evento + morph, o render Liquid già corretto).
    if (this.refs.message.textContent?.trim() !== text) {
      this.refs.message.textContent = text;
    }
  }

  /**
   * Formatta un importo con `shop.money_format` (stesso template usato da Liquid: tutti i mercati sono in EUR).
   * @param {number} minorUnits
   * @param {string} currency
   * @returns {string}
   */
  #formatMoney(minorUnits, currency) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    return formatMoney(minorUnits, format, currency || 'EUR');
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
